import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  FulfillmentPicklist,
  FulfillmentPicklistActionRequest,
  FulfillmentPicklistDetailResponse,
  FulfillmentTask,
} from '@tcvs/shared';
import { useMarketplaceApi } from '../marketplace/marketplace-api';

const QrScanner = lazy(() =>
  import('./QrScanner').then((module) => ({ default: module.QrScanner })),
);

function failure(result: { ok: false; error: string; message?: string }): string {
  return (result.message ?? result.error).replaceAll('_', ' ');
}

function shortPicklistId(value: string): string {
  return value.replace(/^PICKLIST#/, '').slice(-8);
}

function locationLabel(task: FulfillmentTask): string {
  const location = task.location;
  if (location === undefined || location.status === 'UNAVAILABLE') return 'Location unavailable';
  if (location.status === 'NOT_STORED') return 'Not in storage';
  return [
    location.rack?.rackCode === undefined ? undefined : `Rack ${location.rack.rackCode}`,
    location.shelf?.shelfCode === undefined ? undefined : `Shelf ${location.shelf.shelfCode}`,
    location.box?.entityId === undefined ? undefined : `Box ${location.box.entityId.slice(-6)}`,
    location.row === undefined
      ? undefined
      : `Row ${location.row.rowCode ?? location.row.entityId.slice(-6)}`,
    location.batch?.entityId === undefined
      ? undefined
      : `Batch ${location.batch.entityId.slice(-6)}`,
  ]
    .filter((part): part is string => part !== undefined)
    .join(' / ');
}

export function FulfillmentPickWorkbench(): JSX.Element {
  const api = useMarketplaceApi();
  const [readyCount, setReadyCount] = useState(0);
  const [blockedCount, setBlockedCount] = useState(0);
  const [lists, setLists] = useState<FulfillmentPicklist[]>([]);
  const [detail, setDetail] = useState<FulfillmentPicklistDetailResponse | null>(null);
  const [maxItems, setMaxItems] = useState(25);
  const [cardRefs, setCardRefs] = useState<Record<string, string>>({});
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(() => new Set());
  const [scanningTaskId, setScanningTaskId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [tasksResult, listsResult] = await Promise.all([
      api.listFulfillmentTasks(),
      api.listFulfillmentPicklists(),
    ]);
    if (!tasksResult.ok) setError(failure(tasksResult));
    else {
      setReadyCount(tasksResult.data.pickableCount);
      setBlockedCount(tasksResult.data.blockedCount);
    }
    if (!listsResult.ok) setError(failure(listsResult));
    else setLists(listsResult.data.items);
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const loadList = useCallback(
    async (picklistId: string) => {
      setBusy('load');
      setError(null);
      const result = await api.getFulfillmentPicklist(picklistId);
      setBusy(null);
      if (!result.ok) {
        setError(failure(result));
        return;
      }
      setDetail(result.data);
    },
    [api],
  );

  const reconcile = useCallback(async () => {
    setBusy('reconcile');
    setError(null);
    const result = await api.reconcileFulfillmentTasks();
    setBusy(null);
    if (!result.ok) setError(failure(result));
    else {
      setReadyCount(result.data.pickableCount);
      setBlockedCount(result.data.blockedCount);
    }
  }, [api]);

  const generate = useCallback(async () => {
    setBusy('generate');
    setError(null);
    const result = await api.generateFulfillmentPicklist({ maxItems });
    setBusy(null);
    if (!result.ok) {
      setError(failure(result));
      return;
    }
    setDetail(result.data);
    await refresh();
  }, [api, maxItems, refresh]);

  const mutate = useCallback(
    async (body: FulfillmentPicklistActionRequest) => {
      if (detail === null) return;
      setBusy(body.action);
      setError(null);
      const result = await api.mutateFulfillmentPicklist(detail.picklist.picklistId, body);
      setBusy(null);
      if (!result.ok) {
        setError(failure(result));
        return;
      }
      setDetail(result.data);
      await refresh();
    },
    [api, detail, refresh],
  );

  const confirmPick = useCallback(
    async (task: FulfillmentTask, value?: string) => {
      if (detail === null) return;
      const cardRef = (value ?? cardRefs[task.taskId] ?? '').trim();
      if (cardRef === '') {
        setError('Scan or enter the expected card first.');
        return;
      }
      setBusy(task.taskId);
      setError(null);
      const result = await api.confirmFulfillmentPick(detail.picklist.picklistId, {
        taskId: task.taskId,
        cardRef,
      });
      setBusy(null);
      if (!result.ok) {
        setError(failure(result));
        return;
      }
      setCardRefs((current) => ({ ...current, [task.taskId]: '' }));
      setDetail(result.data);
      await refresh();
    },
    [api, cardRefs, detail, refresh],
  );

  const pickSelected = useCallback(
    async (taskIds: string[]) => {
      if (detail === null || taskIds.length === 0) return;
      setBusy('pick-selected');
      setError(null);
      const result = await api.confirmSelectedFulfillmentPicks(detail.picklist.picklistId, {
        taskIds,
      });
      setBusy(null);
      if (!result.ok) {
        setError(failure(result));
        return;
      }
      setSelectedTaskIds((current) => {
        const next = new Set(current);
        taskIds.forEach((taskId) => next.delete(taskId));
        return next;
      });
      setDetail(result.data);
      await refresh();
    },
    [api, detail, refresh],
  );

  const openTasks = useMemo(
    () => detail?.tasks.filter((task) => !['PICKED', 'CANCELED'].includes(task.state)) ?? [],
    [detail],
  );
  const pickableTasks = useMemo(
    () =>
      detail?.picklist.status === 'IN_PROGRESS'
        ? detail.tasks.filter((task) => task.state === 'IN_PROGRESS')
        : [],
    [detail],
  );
  const allPickableSelected =
    pickableTasks.length > 0 && pickableTasks.every((task) => selectedTaskIds.has(task.taskId));
  const selectAllRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const pickableIds = new Set(pickableTasks.map((task) => task.taskId));
    setSelectedTaskIds((current) => {
      const next = new Set([...current].filter((taskId) => pickableIds.has(taskId)));
      return next.size === current.size ? current : next;
    });
  }, [pickableTasks]);

  useEffect(() => {
    if (selectAllRef.current !== null) {
      selectAllRef.current.indeterminate = selectedTaskIds.size > 0 && !allPickableSelected;
    }
  }, [allPickableSelected, selectedTaskIds.size]);

  return (
    <section className="tcvs-card tcvs-fulfillment-workbench">
      {scanningTaskId !== null ? (
        <Suspense fallback={null}>
          <QrScanner
            title="Scan the card being picked"
            onResult={(value) => {
              const task = detail?.tasks.find((item) => item.taskId === scanningTaskId);
              setScanningTaskId(null);
              if (task !== undefined) void confirmPick(task, value);
            }}
            onClose={() => setScanningTaskId(null)}
          />
        </Suspense>
      ) : null}

      <div className="tcvs-fulfillment-heading">
        <div>
          <h3>Fulfillment picking</h3>
          <p className="tcvs-caption">
            Paid sales remain in storage until a picker confirms each assigned card.
          </p>
        </div>
        <div className="tcvs-fulfillment-ready-count">
          <strong>{readyCount}</strong>
          <span>ready to assign</span>
          {blockedCount > 0 ? <span>{blockedCount} need location</span> : null}
        </div>
      </div>

      <div className="tcvs-fulfillment-toolbar">
        <button
          type="button"
          className="tcvs-btn-secondary"
          disabled={busy !== null}
          onClick={() => void reconcile()}
        >
          {busy === 'reconcile' ? 'Refreshing...' : 'Refresh sold orders'}
        </button>
        <label className="tcvs-field tcvs-fulfillment-size">
          <span>Cards per list</span>
          <select value={maxItems} onChange={(event) => setMaxItems(Number(event.target.value))}>
            {[10, 25, 50, 99].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          disabled={busy !== null || readyCount === 0}
          onClick={() => void generate()}
        >
          {busy === 'generate' ? 'Generating...' : 'Generate picklist'}
        </button>
      </div>

      {blockedCount > 0 ? (
        <p className="tcvs-fulfillment-blocked-notice" role="status">
          {blockedCount} sold {blockedCount === 1 ? 'card needs' : 'cards need'} a complete Row
          -&gt; Box -&gt; Shelf location before {blockedCount === 1 ? 'it can' : 'they can'} be
          assigned to a picklist.
        </p>
      ) : null}

      {error !== null ? <p className="tcvs-error">Error: {error}</p> : null}

      <div className="tcvs-fulfillment-layout">
        <aside className="tcvs-fulfillment-lists">
          <h4>Picklists</h4>
          {lists.length === 0 ? <p className="tcvs-muted">No picklists yet.</p> : null}
          {lists.map((list) => (
            <button
              type="button"
              key={list.picklistId}
              className={detail?.picklist.picklistId === list.picklistId ? 'is-active' : ''}
              onClick={() => void loadList(list.picklistId)}
            >
              <span className="tcvs-mono">{shortPicklistId(list.picklistId)}</span>
              <span>{list.status.replaceAll('_', ' ')}</span>
              <small>
                {list.pickedCount}/{list.taskCount} picked
              </small>
            </button>
          ))}
        </aside>

        <div className="tcvs-fulfillment-detail">
          {detail === null ? (
            <p className="tcvs-muted">Generate or open a picklist to begin.</p>
          ) : (
            <>
              <div className="tcvs-fulfillment-detail-heading">
                <div>
                  <h4>Picklist {shortPicklistId(detail.picklist.picklistId)}</h4>
                  <span
                    className={`tcvs-fulfillment-status status-${detail.picklist.status.toLowerCase()}`}
                  >
                    {detail.picklist.status.replaceAll('_', ' ')}
                  </span>
                </div>
                <div className="tcvs-fulfillment-actions">
                  {detail.picklist.status === 'DRAFT' ? (
                    <>
                      <button type="button" onClick={() => void mutate({ action: 'RELEASE' })}>
                        Release
                      </button>
                      <button
                        type="button"
                        className="tcvs-btn-secondary"
                        onClick={() => void mutate({ action: 'CANCEL' })}
                      >
                        Cancel
                      </button>
                    </>
                  ) : null}
                  {detail.picklist.status === 'RELEASED' ? (
                    <>
                      <button type="button" onClick={() => void mutate({ action: 'START' })}>
                        Start picking
                      </button>
                      <button
                        type="button"
                        className="tcvs-btn-secondary"
                        onClick={() => void mutate({ action: 'CANCEL' })}
                      >
                        Cancel
                      </button>
                    </>
                  ) : null}
                  {detail.picklist.status === 'IN_PROGRESS' ? (
                    <button
                      type="button"
                      disabled={openTasks.length > 0}
                      onClick={() => void mutate({ action: 'COMPLETE' })}
                    >
                      Complete picklist
                    </button>
                  ) : null}
                </div>
              </div>

              {detail.picklist.status === 'IN_PROGRESS' && pickableTasks.length > 0 ? (
                <div className="tcvs-fulfillment-pick-toolbar">
                  <label>
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allPickableSelected}
                      disabled={busy !== null}
                      onChange={(event) =>
                        setSelectedTaskIds(
                          event.target.checked
                            ? new Set(pickableTasks.map((task) => task.taskId))
                            : new Set(),
                        )
                      }
                    />
                    Select all unpicked
                  </label>
                  <button
                    type="button"
                    disabled={busy !== null || selectedTaskIds.size === 0}
                    onClick={() => void pickSelected([...selectedTaskIds])}
                  >
                    {busy === 'pick-selected'
                      ? 'Picking...'
                      : `Pick selected (${selectedTaskIds.size})`}
                  </button>
                </div>
              ) : null}

              <ol className="tcvs-fulfillment-task-list">
                {detail.tasks.map((task) => (
                  <li key={task.taskId} className={task.state === 'PICKED' ? 'is-picked' : ''}>
                    <label className="tcvs-fulfillment-task-selection">
                      <input
                        type="checkbox"
                        aria-label={`Select ${task.shortCode ?? task.cardId} for picking`}
                        checked={selectedTaskIds.has(task.taskId)}
                        disabled={
                          busy !== null ||
                          detail.picklist.status !== 'IN_PROGRESS' ||
                          task.state !== 'IN_PROGRESS'
                        }
                        onChange={(event) =>
                          setSelectedTaskIds((current) => {
                            const next = new Set(current);
                            if (event.target.checked) next.add(task.taskId);
                            else next.delete(task.taskId);
                            return next;
                          })
                        }
                      />
                    </label>
                    <div className="tcvs-fulfillment-task-image">
                      {task.thumbnailUrl === undefined ? (
                        <span aria-hidden="true">No image</span>
                      ) : (
                        <img src={task.thumbnailUrl} alt="" />
                      )}
                    </div>
                    <div className="tcvs-fulfillment-task-copy">
                      <div className="tcvs-fulfillment-task-title">
                        <Link to={`/catalog/${task.cardId}`} target="_blank" rel="noreferrer">
                          {task.shortCode ?? task.cardId}
                        </Link>
                        <strong>{task.state.replaceAll('_', ' ')}</strong>
                      </div>
                      <p>{task.title ?? `eBay order ${task.orderId}`}</p>
                      <p className="tcvs-mono tcvs-caption">{locationLabel(task)}</p>
                      {task.location?.currentBatchPosition !== undefined ? (
                        <p className="tcvs-caption">
                          Pick position {task.location.currentBatchPosition} of{' '}
                          {task.location.currentBatchSize}
                        </p>
                      ) : null}
                    </div>
                    <div className="tcvs-fulfillment-task-controls">
                      {detail.picklist.status === 'DRAFT' ? (
                        <button
                          type="button"
                          className="tcvs-link-button"
                          onClick={() =>
                            void mutate({ action: 'REMOVE_TASK', taskId: task.taskId })
                          }
                        >
                          Remove
                        </button>
                      ) : null}
                      {detail.picklist.status === 'IN_PROGRESS' && task.state === 'IN_PROGRESS' ? (
                        <>
                          <button
                            type="button"
                            disabled={busy !== null}
                            onClick={() => void pickSelected([task.taskId])}
                          >
                            {busy === 'pick-selected' ? 'Picking...' : 'Pick'}
                          </button>
                          <input
                            aria-label={`Card ID for ${task.shortCode ?? task.cardId}`}
                            value={cardRefs[task.taskId] ?? ''}
                            onChange={(event) =>
                              setCardRefs((current) => ({
                                ...current,
                                [task.taskId]: event.target.value,
                              }))
                            }
                            placeholder="Scan card ID"
                            className="tcvs-mono"
                          />
                          <button
                            type="button"
                            className="tcvs-btn-secondary"
                            onClick={() => setScanningTaskId(task.taskId)}
                          >
                            Scan
                          </button>
                          <button
                            type="button"
                            disabled={busy !== null}
                            onClick={() => void confirmPick(task)}
                          >
                            {busy === task.taskId ? 'Confirming...' : 'Confirm scanned card'}
                          </button>
                        </>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
